import { usePrivy, useWallets as useEvmWallets, type User } from '@privy-io/react-auth';
import { useEffect, useRef, useState } from 'react';
import { useUserStore, useIsLoggedIn } from '@/app/stores/useUserStore';
import { api } from '@/lib/api';
import { walletApiService } from '@/services/wallet-api';
import { useWallets as useSolanaWallets } from '@privy-io/react-auth/solana';
import { captureLoginFail, captureRequest, captureUserLogin } from '@/lib/analytics/posthog-events';
import { WHITE_LIST_PATHS_WITH_REFRESH } from '@/contexts/global-context';

function privyLoginMethod(user: User): string {
  if (user.google) return 'google';
  if (user.email) return 'email';
  if (user.wallet) return 'wallet';
  return 'unknown';
}

// 全局状态 - 防止重复登录
let globalPrivyLoginState = {
  isProcessing: false,
  currentUserId: '',
};

/** 各挂载点注册的登录成功通知；避免「先抢到 global 锁的 hook 实例没有传 onLoginSuccess」导致回调丢失 */
const privyLoginSuccessListeners = new Set<() => void>();

function notifyPrivyLoginSuccess() {
  privyLoginSuccessListeners.forEach(fn => {
    try {
      fn();
    } catch (e) {
      console.error('Privy onLoginSuccess listener error:', e);
    }
  });
}

/**
 * Privy 登录 Hook
 *
 * 功能：
 * 1. 监听 Privy 认证状态变化
 * 2. 认证成功后自动调用后端 API
 * 3. 存储后端返回的 token
 * 4. 触发登录成功回调
 *
 * 使用方式：
 * const { ready, login, isProcessing } = usePrivyLogin(onSuccess);
 */
export const usePrivyLogin = (onLoginSuccess?: () => void) => {
  const {
    ready,
    authenticated,
    user,
    login,
    logout,
    getAccessToken, // 获取 Privy Access Token
  } = usePrivy();
  const { wallets: evmWallets } = useEvmWallets();
  const { wallets: solanaWallets } = useSolanaWallets();

  const { setToken, fetchUserInfo, setEvmAddress, setSolanaAddress } = useUserStore();
  const isLoggedIn = useIsLoggedIn();
  const [isProcessing, setIsProcessing] = useState(false);
  const onLoginSuccessRef = useRef(onLoginSuccess);
  onLoginSuccessRef.current = onLoginSuccess;

  useEffect(() => {
    const listener = () => {
      onLoginSuccessRef.current?.();
    };
    privyLoginSuccessListeners.add(listener);
    return () => {
      privyLoginSuccessListeners.delete(listener);
    };
  }, []);

  // 监听 Privy 认证状态变化
  useEffect(() => {
    // 条件检查：未就绪、未认证、无用户信息，直接跳过
    if (!ready || !authenticated || !user) {
      return;
    }

    // 如果后端已经登录（有 token），跳过
    if (isLoggedIn) {
      console.log('user already logged in, skip');
      return;
    }

    // 如果全局正在处理中，跳过
    if (globalPrivyLoginState.isProcessing) {
      console.log('Privy login process is ongoing, skip');
      return;
    }

    // 如果当前用户已经处理过，跳过
    if (globalPrivyLoginState.currentUserId === user.id) {
      console.log('current user has been processed, skip');
      return;
    }

    // 通过user 获取是否有privy创建的钱包
    const user_account = user.linkedAccounts;
    // 判断 user_account 中是否有 privy 创建的evm钱包 chainType 为 ethereum， walletClientType 为 privy
    const privyCreatedEvmWallet = user_account.find(account => account.chainType === 'ethereum' && account.walletClientType === 'privy');
    // 判断 user_account 中是否有 privy 创建的solana钱包 chainType 为 solana， walletClientType 为 privy
    const privyCreatedSolanaWallet = user_account.find(account => account.chainType === 'solana' && account.walletClientType === 'privy');
    console.log('privyCreatedEvmWallet1111-----', privyCreatedEvmWallet);
    console.log('privyCreatedSolanaWallet11111-----', privyCreatedSolanaWallet);

    if (!privyCreatedEvmWallet || !privyCreatedSolanaWallet) {
      console.log('privy created evm or solana wallet not found, skip');
      return;
    }

    // 标记为处理中
    globalPrivyLoginState.isProcessing = true;
    globalPrivyLoginState.currentUserId = user.id;

    // 开始后端登录流程
    handlePrivyLogin();
    console.log('执行登录流程');
    console.log('privy login user-----', user);
  }, [ready, authenticated, user, isLoggedIn]);

  /**
   * Privy 登录成功后，调用后端接口
   */
  const handlePrivyLogin = async () => {
    if (!user) return;

    console.log('🚀 Privy 登录成功，开始后端认证流程', {
      userId: user.id,
      email: user.google?.email || user.email?.address,
      wallet: user.wallet?.address,
    });

    setIsProcessing(true);

    try {
      // 1. 获取 Privy Access Token
      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error('无法获取 Privy Access Token');
      }

      console.log('✓ 获取到 Privy Access Token');

      // 2. 调用后端登录 API
      const response = await walletApiService.privyLogin({ accessToken });

      console.log('✓ 后端登录响应:', response);

      if (response.code !== 200) {
        throw new Error(response.msg || '后端登录失败');
      }

      const { token, user: backendUser } = response.data || response;

      if (!token) {
        throw new Error('后端返回数据缺少 token');
      }

      // 3. 存储登录信息到全局 store
      await api.resetToken(token);
      setToken(token);

      // 分别存储 evm 和 solana 钱包地址
      const evmWalletAddress = backendUser.evmWalletAddress;
      const solanaWalletAddress = backendUser.solWalletAddress;

      setEvmAddress(evmWalletAddress);
      setSolanaAddress(solanaWalletAddress);
      // 重新获取用户信息
      await fetchUserInfo();

      // 清除 juvo 信息
      localStorage.removeItem('billion-userInfo');
      localStorage.removeItem('billion-liveResult');

      console.log('✅ Privy 后端登录成功！', {
        token: token.slice(0, 20) + '...',
        user: backendUser,
      });

      // 上报登录成功
      captureRequest({
        action: 'login',
        status: 'success',
        type: user ? privyLoginMethod(user) : 'unknown',
      });
      captureUserLogin({ login_method: privyLoginMethod(user) });
      // 执行成功回调（广播给所有挂载了本 hook 的实例，避免无回调实例抢到 global 锁时丢失弹窗等处的回调）
      notifyPrivyLoginSuccess();
      // 如果是直播页，直接刷新
      const redirectRefreshPath = WHITE_LIST_PATHS_WITH_REFRESH.find(path => location.pathname.startsWith(path));
      if (redirectRefreshPath) {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ Privy 后端登录失败:', error);
      // 上报登录失败
      captureRequest({
        action: 'login',
        status: 'fail',
        msg: error instanceof Error ? error.message : 'unknown',
        type: user ? privyLoginMethod(user) : 'unknown',
        stage: 'backend_login',
      });
      captureLoginFail({
        fail_reason: error instanceof Error ? error.message : 'unknown',
        login_method: privyLoginMethod(user),
      });

      // 登录失败，登出 Privy
      await logout();

      // 提示用户
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      alert(`登录失败: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
      globalPrivyLoginState.isProcessing = false;
    }
  };

  /**
   * 手动触发登录
   * 只有已认证时才退出登录，然后打开 Privy 登录弹窗
   */
  const handleLogin = async () => {
    try {
      console.log('🔄 准备登录...');

      // 只有在已认证时才调用 logout
      if (authenticated) {
        console.log('🚪 退出旧的 Privy 会话...');
        await logout();

        // 如果有连接的钱包，需要逐个断开
        for (const wallet of evmWallets) {
          if (wallet.address) {
            await wallet.disconnect();
          }
        }
        for (const wallet of solanaWallets) {
          if (wallet.address) {
            await wallet.disconnect();
          }
        }
      }

      // 重置全局状态
      globalPrivyLoginState.isProcessing = false;
      globalPrivyLoginState.currentUserId = '';

      console.log('✅ 打开 Privy 登录弹窗');

      // 打开登录弹窗
      await login();
    } catch (error) {
      console.error('打开 Privy 登录弹窗失败:', error);
      // 上报请求结果
      captureRequest({
        action: 'login',
        status: 'fail',
        msg: error instanceof Error ? error.message : 'unknown',
        type: user ? privyLoginMethod(user) : 'unknown',
      });
      // 上报登录失败
      captureLoginFail({
        fail_reason: error instanceof Error ? error.message : 'unknown',
        login_method: user ? privyLoginMethod(user) : 'unknown',
      });
    }
  };

  return {
    ready, // Privy 是否就绪
    authenticated, // 是否已认证
    user, // Privy 用户信息
    login: handleLogin, // 登录方法
    logout, // 登出方法
    isProcessing, // 是否正在处理后端登录
  };
};
